import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { Container, Row, Col, Card, CardTitle, CardBody } from "reactstrap";
import { Link } from "react-router-dom";
import classNames from "classnames";
import StackedColumnChart from "./StackedColumnChart";
import moment from "moment";
import { getChartsData as onGetChartsData } from "../../store/actions";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import DatePicker from "react-datepicker";
import UserUsageChart from '../MyAccount/unitsChart';
import "react-datepicker/dist/react-datepicker.css";

const Dashboard = (props) => {

  const [startDate, setStartDate] = useState(moment().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().endOf('month').format('YYYY-MM-DD'));

  // Add date change handlers
  const handleStartDateChange = (date) => {
    setStartDate(moment(date).format('YYYY-MM-DD'));
  };

  const handleEndDateChange = (date) => {
    setEndDate(moment(date).format('YYYY-MM-DD'));
  };

  const handleDateSubmit = () => {
    dispatch(onGetChartsData({ from: startDate, to: endDate }));
  };

  const [weeklyTotal, setWeeklyTotal] = useState(0);
  const [monthlyTotal, setMonthlyTotal] = useState(0);

  const weekData = [
    { name: "Safaricom", data: [0, 0, 0, 0, 0, 0, 0] },
    { name: "Airtel", data: [0, 0, 0, 0, 0, 0, 0] },
    { name: "Telkom", data: [0, 0, 0, 0, 0, 0, 0] },
  ];

  const monthData = [
    { name: "Safaricom", data: Array.from({ length: 31 }, () => 0) },
    { name: "Airtel", data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0] },
    { name: "Telkom", data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0] },
  ];

  const yearData = [
    { name: "Safaricom", data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0] },
    { name: "Airtel", data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0] },
    { name: "Telkom", data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0] },
  ];

  const CombinedProperties = createSelector(
    (state) => state.Login,
    (state) => state.Dashboard,
    (login, dashboard) => ({
      isAuthenticated: login.isAuthenticated,
      token: login.sessionToken,
      chartsData: dashboard.chartsData,
    })
  );

  const { chartsData, isAuthenticated, token } = useSelector(CombinedProperties);

  // const DashboardProperties = createSelector(
  //   (state) => state.Dashboard,
  //   (dashboard) => ({
  //     chartsData: dashboard.chartsData,
  //   })
  // );

  // const {  } = useSelector(DashboardProperties);
  const normalizeData = (chartsData, startDate, endDate, periodType) => {
    const dateRange = [];
    let current = moment(startDate);

    if (periodType === "weekly") {
      while (current.isBefore(endDate) || current.isSame(endDate)) {
        dateRange.push(current.format("YYYY-MM-DD"));
        current.add(1, "day");
      }
    } else if (periodType === "monthly") {
      while (current.isBefore(endDate) || current.isSame(endDate)) {
        dateRange.push(current.format("YYYY-MM-DD"));
        current.add(1, "day");
      }
    } else if (periodType === "yearly") {
      for (let i = 0; i < 12; i++) {
        dateRange.push(current.startOf("month").format("YYYY-MM"));
        current.add(1, "month");
      }
    }

    const normalizedData = dateRange.map((date) => {
      const matching = chartsData.find((data) =>
        periodType === "yearly" ? date === moment(data.Day).format("YYYY-MM") : date === data.Day
      );
      return { Day: date, Count: matching ? matching.Count : 0 };
    });

    return normalizedData;
  };


  // Function to map day of the month to a day of the week index (0-6, Sunday-Saturday)
  const getDayOfWeekIndex = (dayString) => {
    const date = new Date(dayString);
    return date.getDay();  // Returns 0 (Sunday) through 6 (Saturday)
  };

  // Function to get the month index (0 = January, 1 = February, ..., 11 = December)
  const getMonthIndex = (dayString) => {
    const date = new Date(dayString);
    return date.getMonth(); // Returns month index (0 for January, 1 for February, ...)
  };

  const [periodData, setPeriodData] = useState(monthData); // Default to monthly data
  const [periodType, setPeriodType] = useState("monthly"); // Default to "monthly"

  const chartRef = useRef();

  const calculateTotals = () => {
    let weeklySum = 0;
    let monthlySum = 0;

    if (!chartsData) return;

    chartsData.forEach(({ Day, Count }) => {
      const dayMoment = moment(Day);
      const isWithinRange = dayMoment.isBetween(startDate, endDate, null, '[]');

      if (isWithinRange) {
        if (periodType === "weekly") weeklySum += Count;
        else if (periodType === "monthly" || periodType === "yearly") monthlySum += Count;
      }
    });

    setWeeklyTotal(weeklySum);
    setMonthlyTotal(monthlySum);
  };

  useEffect(() => {
    if (chartsData) calculateTotals();
  }, [chartsData, startDate, endDate, periodType]);

  const reports = [
    {
      title: periodType === "weekly" ? "Weekly Messages" : "Messages in Range",
      iconClass: "bx bx-envelope-open",
      description: periodType === "weekly" ? weeklyTotal.toLocaleString() : monthlyTotal.toLocaleString(),
    },
    {
      title: periodType === "monthly" ? "Monthly Messages" : "Total Messages",
      iconClass: "bx bx-line-chart",
      description: monthlyTotal.toLocaleString(),
    },
  ];

  const onChangeChartPeriod = (pType) => {
    setPeriodType(pType);

    let updatedData = [];
    let updatedCategories = [];

    switch (pType) {
      case "weekly":
        setStartDate(moment().startOf('week').format('YYYY-MM-DD'));
        setEndDate(moment().endOf('week').format('YYYY-MM-DD'));

        // Reset weekData to all zeros
        weekData.forEach(provider => {
          provider.data.fill(0);
        });

        // Update week data based on chartsData
        chartsData?.forEach(({ Day, Count }) => {
          const dayOfWeek = getDayOfWeekIndex(Day); // Get the day index (0-6)
          if (dayOfWeek >= 0 && dayOfWeek < 7) {
            weekData[0].data[dayOfWeek] = Count; // Update Safaricom's data
          }
        });

        updatedCategories = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        updatedData = weekData;
        break;

      case "monthly":
        setStartDate(moment().startOf('month').format('YYYY-MM-DD'));
        setEndDate(moment().endOf('month').format('YYYY-MM-DD'));

        // Reset monthData to all zeros
        monthData.forEach(provider => {
          provider.data.fill(0);
        });

        // Update month data based on chartsData
        chartsData?.forEach(({ Day, Count }) => {
          const day = new Date(Day).getDate() - 1; // Convert Day to index (0-based)
          if (day >= 0 && day < 31) {
            monthData[0].data[day] = Count; // Update Safaricom's data
          }
        });

        updatedCategories = Array.from(
          { length: moment().daysInMonth() },
          (_, i) => i + 1
        );
        updatedData = monthData;
        break;

      case "yearly":
        setStartDate(moment().startOf('year').format('YYYY-MM-DD'));
        setEndDate(moment().endOf('year').format('YYYY-MM-DD'));

        // Reset yearData to all zeros
        yearData.forEach(provider => {
          provider.data.fill(0);
        });

        // Update year data based on chartsData
        chartsData?.forEach(({ Day, Count }) => {
          const monthIndex = getMonthIndex(Day); // Get the month index (0-11)
          if (monthIndex >= 0 && monthIndex < 12) {
            yearData[0].data[monthIndex] += Count; // Accumulate the count for the respective month
          }
        });

        updatedCategories = [
          "Jan", "Feb", "Mar", "Apr", "May", "Jun",
          "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];
        updatedData = yearData;
        break;

      default:
        break;
    }

    setPeriodData(updatedData);

    if (chartRef.current) {
      chartRef.current.updateChartOptions(updatedCategories);
    }
  };

  useEffect(() => {
    let updatedData = [];
    let updatedCategories = [];

    switch (periodType) {
      case "weekly":
        // Reset weekData to zeros
        weekData.forEach(provider => {
          provider.data.fill(0);
        });

        // Update week data based on chartsData
        chartsData?.forEach(({ Day, Count }) => {
          const dayOfWeek = getDayOfWeekIndex(Day); // Get the day index (0-6)
          if (dayOfWeek >= 0 && dayOfWeek < 7) {
            weekData[0].data[dayOfWeek] = Count; // Update Safaricom's data
          }
        });

        updatedCategories = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        updatedData = weekData;
        break;

      case "monthly":
        // Reset monthData to zeros
        monthData.forEach(provider => {
          provider.data.fill(0);
        });

        // Update month data based on chartsData
        chartsData?.forEach(({ Day, Count }) => {
          const day = new Date(Day).getDate() - 1; // Convert Day to index (0-based)
          if (day >= 0 && day < 31) {
            monthData[0].data[day] = Count; // Update Safaricom's data
          }
        });

        const daysInMonth = moment().daysInMonth(); // Number of days in the current month
        updatedCategories = Array.from({ length: daysInMonth }, (_, i) => i + 1);

        // For previous months, fill in all the days
        if (moment().month() !== moment(startDate).month()) {
          updatedData = monthData;
        } else {
          // For the current month, only include data up to the current day
          updatedData = monthData.map(provider => ({
            name: provider.name,
            data: provider.data.slice(0, new Date().getDate()), // Data up to the current day
          }));
        }

        break;

      case "yearly":
        // Reset yearData to zeros
        yearData.forEach(provider => {
          provider.data.fill(0);
        });

        // Update year data based on chartsData
        chartsData?.forEach(({ Day, Count }) => {
          const monthIndex = getMonthIndex(Day); // Get the month index (0-11)
          if (monthIndex >= 0 && monthIndex < 12) {
            yearData[0].data[monthIndex] += Count; // Accumulate the count for the respective month
          }
        });

        updatedCategories = [
          "Jan", "Feb", "Mar", "Apr", "May", "Jun",
          "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];

        updatedData = yearData;
        break;

      default:
        break;
    }

    setPeriodData(updatedData);

    if (chartRef.current) {
      chartRef.current.updateChartOptions(updatedCategories);
    }
  }, [chartsData, periodType, startDate, endDate]);


  const dispatch = useDispatch();


  useEffect(() => {
    if (token, startDate && endDate) {
      dispatch(onGetChartsData({ from: startDate, to: endDate }));
    }
  }, [dispatch, startDate, endDate, token]);


  document.title = "Dashboard | KenGen SMS Portal";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={props.t("Dashboards")} breadcrumbItem={props.t("Dashboard")} />
          <Row>
            {(reports || []).map((report, key) => (
              <Col md="6" key={"_col_" + key}>
                <Card className="mini-stats-wid">
                  <CardBody>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <p className="text-muted fw-medium">{report.title}</p>
                        <h4 className="mb-0">{report.description}</h4>
                      </div>
                      <div className="avatar-sm rounded-circle bg-primary align-self-center mini-stat-icon">
                        <span className="avatar-title rounded-circle bg-primary">
                          <i className={"bx " + report.iconClass + " font-size-24"}></i>
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
          <Row>
            <Col xl="12">
              <Card>
                <CardBody>
                  <div className="d-sm-flex flex-wrap">
                    <h4 className="card-title mb-4">SMS Sent</h4>
                    <div className="ms-auto">
                      <ul className="nav nav-pills">
                        <li className="nav-item">
                          <Link
                            to="#"
                            className={classNames({ active: periodType === "weekly" }, "nav-link")}
                            onClick={() => onChangeChartPeriod("weekly")}
                          >
                            Week
                          </Link>
                        </li>
                        <li className="nav-item">
                          <Link
                            to="#"
                            className={classNames({ active: periodType === "monthly" }, "nav-link")}
                            onClick={() => onChangeChartPeriod("monthly")}
                          >
                            Month
                          </Link>
                        </li>
                        <li className="nav-item">
                          <Link
                            to="#"
                            className={classNames({ active: periodType === "yearly" }, "nav-link")}
                            onClick={() => onChangeChartPeriod("yearly")}
                          >
                            Year
                          </Link>
                        </li>
                      </ul>
                    </div>
                    {/* Date Picker Controls */}
                    <div className="date-filters d-flex align-items-center">
                      <DatePicker
                        selected={moment(startDate).toDate()}
                        onChange={handleStartDateChange}
                        dateFormat="yyyy-MM-dd"
                        selectsStart
                        startDate={startDate}
                        endDate={endDate}
                        className="form-control"
                        placeholderText="Select start date"
                        style={{ maxWidth: "100px" }}
                      />
                      <span className="mx-2">to</span>
                      <DatePicker
                        selected={moment(endDate).toDate()}
                        onChange={handleEndDateChange}
                        dateFormat="yyyy-MM-dd"
                        selectsEnd
                        startDate={startDate}
                        endDate={endDate}
                        className="form-control"
                        placeholderText="Select end date"
                        style={{ maxWidth: "50px" }}
                      />
                      <button onClick={handleDateSubmit} className="btn btn-primary ms-2">
                        Submit
                      </button>
                    </div>
                  </div>
                  <StackedColumnChart
                    ref={chartRef}
                    periodData={periodData}
                    dataColors='["--bs-primary", "--bs-danger", "--bs-warning"]'
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <UserUsageChart />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

Dashboard.propTypes = {
  t: PropTypes.any,
  chartsData: PropTypes.any,
  onGetChartsData: PropTypes.func,
};

export default withTranslation()(Dashboard);
